import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Page from '../components/Page';
import ReactGA from 'react-ga4';

const PLANS = [
  {
    icon: '🌱',
    name: 'Clubhouse',
    price: 19.99,
    tagline: 'Perfect for small congregations just getting started.',
    templates: 5,
    sms: '600',
    emails: '1,000',
    admins: '1',
    cta: 'Start 3-Month Trial',
    featured: false,
  },
  {
    icon: '🚀',
    name: 'Startup',
    price: 39.99,
    tagline: 'For growing churches coordinating multiple ministries.',
    templates: 10,
    sms: '1,300',
    emails: '2,500',
    admins: '3',
    cta: 'Get Started',
    featured: false,
  },
  {
    icon: '👥',
    name: 'Company',
    price: 59.99,
    tagline: 'Best for established churches with active volunteer programs.',
    templates: 20,
    sms: '2,100',
    emails: '4,000',
    admins: '10',
    cta: 'Get Started',
    featured: true,
  },
  {
    icon: '🏛️',
    name: 'Enterprise',
    price: 79.99,
    tagline: 'For large or multi-campus churches with no limits needed.',
    templates: 'Unlimited',
    sms: '3,000',
    emails: '6,000',
    admins: 'Unlimited',
    cta: 'Get Started',
    featured: false,
  },
];

const FAQ = [
  {
    question: 'Is billing monthly or yearly?',
    answer:
      "You'll receive an invoice once a month and can cancel your plan at any time — no long-term contracts.",
  },
  {
    question: 'How do SMS and email credits work?',
    answer:
      'Each time you send a notification via SMS or email, one credit is deducted. Your monthly credit limit resets at the start of every billing period. If you exceed your limit, a small overdraft fee applies — or you can disable overdrafts in your account settings.',
  },
  {
    question: 'Do unused credits roll over?',
    answer:
      "Yes — half of your unused credits automatically roll over to the next billing period. For example, if you're on Clubhouse (600 SMS/month) and used only 200, 300 of the remaining 400 roll over. You'd start the next month with 900 SMS credits.",
  },
  {
    question: "What's an SMS segment?",
    answer:
      "SMS credits count in segments — think of them as chunks of a message. A short SMS uses 1 segment; a longer one might use 2 or 3. We recommend keeping messages concise to get the most out of your monthly credits.",
  },
  {
    question: 'Can I cancel at any time?',
    answer:
      'Absolutely. You can manage or cancel your subscription at any time from the My Account page. No cancellation fees.',
  },
  {
    question: 'When does my subscription expire?',
    answer:
      'You can view your current plan details, billing date, and subscription status anytime on the My Account page.',
  },
  {
    question: 'What if I go over my credit limit?',
    answer:
      'An overdraft fee applies and appears on your next invoice. SMS overdraft: $5.00 for 125 additional credits. Email overdraft: $1.00 for 100 additional credits. You can disable overdrafts in settings, though we recommend keeping them on so your notifications never fail to deliver.',
  },
];

export default function PlansPage() {
  useEffect(() => {
    ReactGA.send('pageview');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <Page seo={{
      title: 'Scheduly Pricing — Plans Starting at $19.99/mo',
      description: 'Simple, transparent pricing for church scheduling software. Every plan includes unlimited events and members. 3-month free trial, 30-day money-back guarantee.',
      canonical: 'https://scheduly.org/plans',
      ogTitle: 'Scheduly Pricing — Plans Starting at $19.99/mo',
      ogDescription: 'Church scheduling software with plans for any size congregation. 3-month free trial included.',
      twitterCard: 'summary',
      twitterTitle: 'Scheduly Pricing — Plans Starting at $19.99/mo',
      twitterDescription: 'Church scheduling software with plans for any size congregation. 3-month free trial included.',
    }}>
      {/* HEADER */}
      <div
        className="text-center px-6 pt-20 pb-14"
        style={{
          background: 'linear-gradient(180deg, #eef2ff 0%, #f8f7ff 60%, #f8fafc 100%)',
        }}
      >
        <span className="inline-flex items-center gap-1.5 bg-indigo-100 text-indigo-700 text-xs font-bold px-4 py-1.5 rounded-full mb-5 tracking-wide">
          💜 Simple, transparent pricing
        </span>
        <h1 className="text-5xl font-black text-slate-900 tracking-tight leading-tight mb-4">
          Plans that grow<br className="hidden sm:block" /> with your church
        </h1>
        <p className="text-lg text-slate-500 max-w-lg mx-auto leading-relaxed mb-6">
          Every plan includes unlimited events, unlimited members, and all core
          features. Only pay more as your communication needs grow.
        </p>
        <span className="inline-flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-sm font-semibold px-5 py-2 rounded-full">
          🛡️ 30-day money-back guarantee on all plans
        </span>
      </div>

      {/* PLAN CARDS */}
      <div className="px-6 py-14 max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {PLANS.map((plan) => (
          <PlanCard key={plan.name} plan={plan} />
        ))}
      </div>

      {/* CONTACT */}
      <div className="border-t border-slate-100 px-6 py-16">
        <div className="max-w-xl mx-auto text-center">
          <p className="text-xs font-bold text-indigo-600 uppercase tracking-widest mb-3">
            Get in Touch
          </p>
          <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-3">
            Questions? Want a demo?
          </h2>
          <p className="text-base text-slate-500 leading-relaxed mb-6">
            Not sure which plan is right for your church, or want to see Scheduly in action first? Send us an email and we'll get back to you quickly.
          </p>
          <a
            href="mailto:schedulyevents@gmail.com"
            className="inline-flex items-center gap-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-sm px-6 py-3 rounded-xl no-underline transition-colors"
          >
            ✉ schedulyevents@gmail.com
          </a>
        </div>
      </div>

      {/* FAQ */}
      <div className="max-w-2xl mx-auto px-6 pb-24">
        <h2 className="text-3xl font-black text-slate-900 tracking-tight mb-2">
          Frequently asked questions
        </h2>
        <p className="text-base text-slate-500 mb-8">
          Everything you need to know about how billing works.
        </p>
        <div>
          {FAQ.map((item) => (
            <FaqItem key={item.question} {...item} />
          ))}
        </div>
      </div>
    </Page>
  );
}

/* ─── Sub-components ─────────────────────────────── */

function PlanCard({ plan }) {
  const navigate = useNavigate();
  const { icon, name, price, tagline, templates, sms, emails, admins, cta, featured } = plan;

  const features = [
    'Website hosting',
    'Event automation',
    'Event reports',
    'Unlimited events',
    'Unlimited members',
    `${templates} event templates`,
    `${sms} SMS / month`,
    `${emails} emails / month`,
    `${admins} admin account${admins === '1' ? '' : 's'}`,
  ];

  return (
    <div
      className={`relative bg-white rounded-2xl p-7 flex flex-col ${
        featured
          ? 'border-2 border-indigo-500 shadow-xl shadow-indigo-100'
          : 'border border-slate-200'
      }`}
    >
      {featured && (
        <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap">
          ⭐ Most Popular
        </span>
      )}

      <div className="text-2xl mb-3">{icon}</div>
      <h2 className="text-lg font-black text-slate-900 mb-1">{name}</h2>
      <p className="text-xs text-slate-500 mb-5 leading-relaxed">{tagline}</p>

      <div className="flex items-baseline gap-1 mb-5">
        <span className="text-4xl font-black text-slate-900 tracking-tight">
          ${Math.floor(price)}
        </span>
        <span className="text-sm text-slate-400">.{String(price.toFixed(2)).split('.')[1]} / mo</span>
      </div>

      <hr className="border-slate-100 mb-5" />

      <ul className="flex flex-col gap-2.5 mb-7 flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-xs text-slate-600">
            <span className="text-indigo-500 font-bold mt-0.5">✓</span>
            {f}
          </li>
        ))}
      </ul>

      <button
        onClick={() => navigate(`/checkout?plan=${name}`)}
        className={`w-full py-3 rounded-xl text-sm font-bold transition-colors cursor-pointer ${
          featured
            ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-200'
            : 'bg-slate-100 hover:bg-slate-200 text-slate-900'
        }`}
      >
        {cta}
      </button>
    </div>
  );
}

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-slate-100">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex justify-between items-center py-5 text-left cursor-pointer bg-transparent border-none"
      >
        <span
          className={`text-sm font-semibold transition-colors ${
            open ? 'text-indigo-600' : 'text-slate-900'
          }`}
        >
          {question}
        </span>
        <span
          className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ml-4 transition-colors ${
            open ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-400'
          }`}
        >
          {open ? '−' : '+'}
        </span>
      </button>
      {open && (
        <p className="text-sm text-slate-500 leading-relaxed pb-5">{answer}</p>
      )}
    </div>
  );
}
